/**
 * ===================================================
 * Module: frontend.src.utils.pageRangeParser.ts
 * Description: Utility to parse page range strings (e.g. "1-8, 10, 14-19")
 *              into sorted, deduplicated arrays of page numbers.
 * Owner: Kawada Technosystem Co Ltd
 * All Rights Reserved
 * ===================================================
 */

export interface PageRangeResult {
  pages: number[];
  isValid: boolean;
  error?: string;
}

/**
 * Parse a page range string into an array of page numbers.
 * 
 * Supported formats:
 *   "1-8, 10, 14-19"  → [1,2,3,4,5,6,7,8,10,14,15,16,17,18,19]
 *   "5"               → [5]
 *   "1-3"             → [1,2,3]
 *   "3, 1, 2"         → [1,2,3]  (sorted + deduplicated)
 * 
 * @param rangeString - The range string to parse
 * @param totalPages - Maximum valid page number
 * @returns PageRangeResult with sorted unique page numbers
 */
export function parsePageRange(rangeString: string, totalPages: number): PageRangeResult {
  const trimmed = rangeString.trim();

  if (!trimmed) {
    return { pages: [], isValid: false, error: 'emptyRange' };
  }

  const pages = new Set<number>();
  // Split by comma or space-separated groups
  const parts = trimmed.split(/[,、]+/).map(p => p.trim()).filter(Boolean);

  for (const part of parts) {
    // Check for range (e.g. "1-8")
    const rangeMatch = part.match(/^(\d+)\s*[-–—]\s*(\d+)$/);
    if (rangeMatch) {
      const start = parseInt(rangeMatch[1], 10);
      const end = parseInt(rangeMatch[2], 10);

      if (isNaN(start) || isNaN(end)) {
        return { pages: [], isValid: false, error: 'invalidFormat' };
      }
      if (start > end) {
        return { pages: [], isValid: false, error: 'invalidRange' };
      }
      if (start < 1) {
        return { pages: [], isValid: false, error: 'pageBelow1' };
      }
      if (end > totalPages) {
        return { pages: [], isValid: false, error: 'pageExceedsTotal' };
      }

      for (let i = start; i <= end; i++) {
        pages.add(i);
      }
    } else {
      // Single page number
      const num = parseInt(part, 10);
      if (isNaN(num) || part !== String(num)) {
        return { pages: [], isValid: false, error: 'invalidFormat' };
      }
      if (num < 1) {
        return { pages: [], isValid: false, error: 'pageBelow1' };
      }
      if (num > totalPages) {
        return { pages: [], isValid: false, error: 'pageExceedsTotal' };
      }
      pages.add(num);
    }
  }

  if (pages.size === 0) {
    return { pages: [], isValid: false, error: 'emptyRange' };
  }

  return {
    pages: Array.from(pages).sort((a, b) => a - b),
    isValid: true,
  };
}
